import { Favorite, Home, Search } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { useSearchDialog } from '@tunasong/search'
import {
  ShortcutBox,
  UserAvatar,
  VBox,
  shortcuts,
  useFavorites,
  useHover,
  useNavigate,
  Typography,
  useTunaMediaQuery,
} from '@tunasong/ui-lib'
import cn from 'classnames'
import { useRef, type FC } from 'react'
import styles from './app-sidebar.module.css'
import { ToolsMenuItems } from './tools-menu-items.js'

export interface AppSideBarProps {
  entity?: Persisted<Entity>
}

export const AppSideBar: FC<AppSideBarProps> = props => {
  const { entity } = props

  const { favorites } = useFavorites()

  const { userId } = useCurrentUser()

  const isSmall = useTunaMediaQuery('(max-width: 600px)')
  const avatarSize = isSmall ? 'tiny' : 'small'

  const { dialog: searchDialog, setOpen: setSearchOpen, open: searchOpen } = useSearchDialog(entity)

  const navigate = useNavigate()

  const ref = useRef<HTMLDivElement>(null)
  const hover = useHover(ref.current)

  return (
    <VBox ref={ref} className={styles.sidebar}>
      <div className={styles.userRow}>
        <UserAvatar size={avatarSize} userId={userId} aria-label="user" showName={true} />
      </div>

      <div className={styles.nav}>
        <button
          onClick={() => navigate('/app')}
          className={cn(styles.menuItem, location.pathname === '/app' && styles['menuItem--active'])}
        >
          <Home />
          <span>Home</span>
        </button>
        <button
          onClick={() => setSearchOpen(true)}
          className={cn(styles.menuItem, searchOpen && styles['menuItem--active'])}
        >
          <Search />
          <span className={styles.spacer}>Search</span>
          <ShortcutBox shortcut={shortcuts.Search} showDescription={false} />
        </button>

        <div className={cn(styles.menuItem, styles['menuItem--muted'])}>
          <Typography variant="caption">Tools</Typography>
        </div>

        <ToolsMenuItems variant="caption" />

        <div className={styles.spacer} />

        <div className={cn(styles.menuItem, styles['menuItem--muted'])}>
          <Favorite size={16} />
          <Typography variant="caption">Favorites</Typography>
        </div>

        {favorites.map(favorite => (
          <button key={favorite.path} className={styles.menuItem} onClick={() => navigate(favorite.path)}>
            <span style={{ color: hover ? favorite.color : 'var(--tuna-palette-action-disabled)' }}>
              <favorite.Icon />
            </span>
            <Typography variant="caption">{favorite.name}</Typography>
          </button>
        ))}
        {favorites.length === 0 ? (
          <div className={cn(styles.menuItem, styles['menuItem--muted'])}>
            <Typography variant="caption">No favorites</Typography>
          </div>
        ) : null}
      </div>

      {searchDialog}
    </VBox>
  )
}
